import type { SpotListItem } from "@divespots/contracts";
import { isClosedOn, pickLocale } from "@divespots/contracts";
import { Ban, CircleDot, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "../components/Badge.tsx";
import { formatDate, formatMetres, formatMonthDay } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";

interface Props {
  spot: Pick<
    SpotListItem,
    "difficulty" | "maxDepthM" | "closedPeriods" | "permanentClosure" | "objectCount"
  >;
  /** Injectable for tests; defaults to the current time. */
  now?: Date | undefined;
}

/** Badges row: difficulty, max depth, closed-now with reason, object count. One row, wraps. */
export function SpotBadges({ spot, now }: Props) {
  const { t } = useTranslation();
  const locale = useLocale();
  const forGood = spot.permanentClosure !== null;
  // closed for good takes precedence over the seasonal "currently closed" badge
  const closed = forGood ? null : isClosedOn(spot.closedPeriods, now ?? new Date());
  let closedText: string | null = null;
  if (closed) {
    const date = formatMonthDay(closed.to, locale);
    const reason = pickLocale(closed.reason, locale);
    closedText = reason
      ? t("spot.closedNowReason", { reason, date })
      : t("spot.closedNowUntil", { date });
  }
  return (
    <div className="ds-sheet__badges">
      {spot.difficulty ? <Badge>{t(`difficulty.${spot.difficulty}`)}</Badge> : null}
      {spot.maxDepthM !== null ? (
        <Badge variant="depth">
          {t("spot.maxDepthBadge", { value: formatMetres(spot.maxDepthM, locale, 0) })}
        </Badge>
      ) : null}
      {forGood ? (
        <Badge variant="closed" icon={<Ban aria-hidden="true" strokeWidth={1.75} />}>
          {t("spot.closedForGood")}
        </Badge>
      ) : null}
      {closedText ? (
        <Badge variant="closed" icon={<TriangleAlert aria-hidden="true" strokeWidth={1.75} />}>
          {closedText}
        </Badge>
      ) : null}
      {spot.objectCount > 0 ? (
        <Badge variant="object" icon={<CircleDot aria-hidden="true" strokeWidth={1.75} />}>
          {t("spot.objectCount", { count: spot.objectCount })}
        </Badge>
      ) : null}
    </div>
  );
}

/** The "closed for good" note under the badges: since when and why (shown at every detent). */
export function PermanentClosureNote({ spot }: { spot: Pick<SpotListItem, "permanentClosure"> }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const c = spot.permanentClosure;
  if (!c) return null;
  const reason = pickLocale(c.reason, locale);
  return (
    <p className="ds-text-footnote text-label-secondary m-0 spot-closed-note" role="note">
      {c.since ? (
        <span>{t("spot.closedForGoodSince", { date: formatDate(c.since, locale) })}</span>
      ) : null}
      {c.since && reason ? " · " : null}
      {reason ? <span>{t("spot.closedForGoodReason", { reason })}</span> : null}
    </p>
  );
}
